import * as Sentry from "@sentry/react";
import { QueryClient } from "react-query";
import { toast } from "react-toastify";

import axios from "axios";
import {
  CLEAR_MAP_ID,
  DATA_SVC_RS_ADDRESS,
  MAP_RS_SERVICE_ADDRESS,
  MAP_SERVICE_ADDRESS
} from "constants/app.constants";
import type { IMapRequest } from "stores/map";
import * as z from "zod";

import { TxnT, XdaConstraintT } from "models";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";
import {
  XdaProjectionColorEntry,
  XdaProjectionRequest,
  XdaProjectionResponse,
  XdaProjectionResponseSchema
} from "models/schemas/xdaProjection.schema";

import type { EntitiesInGroupModel } from "components/map/contexts/MapContextState";

import { BubbleOptionsT } from "../components/map/toolbar/MapVis";
import { Result } from "./dataSource";
import {
  decodeEntitiesOnMapPayload,
  toEntitiesOnMapArrayBuffer
} from "./entitiesOnMapDecoder";

export type { XdaProjectionColorEntry, XdaProjectionRequest, XdaProjectionResponse };

const mapServiceEndpoint = process.env.REACT_APP_MAP_SERVICE;
const dataRoot = DATA_SVC_RS_ADDRESS;
const xdaRootUrl = process.env.REACT_APP_XDA_SERVICE;

export interface MapLegendItem {
  color: string;
  paletteColor?: string;
  count: number;
  groupTitle: string;
  inFilter: boolean;
  title: string;
}

export interface IMapLegend {
  legendItems: MapLegendItem[];
  title: string;
}

export interface IBound {
  sw: number[];
  ne: number[];
}

export interface IMapResult {
  id: string;
  facilityId: string;
  bBox: IBound;
  legend: IMapLegend;
  facilityLegend: IMapLegend;
  error?: string;
  filterId?: string;
}

export const EMPTY_MAP_RESULT = {
  error: null,
  id: "",
  legend: { legendItems: [], title: "" },
  bbox: null,
  facilityLegend: { legendItems: [], title: "" },
  facilityId: "",
  bBox: {
    sw: [],
    ne: []
  }
} as IMapResult;

async function getTxnId(request: IMapRequest, cancelToken): Promise<IMapResult> {
  try {
    const response = await axios.post(`${MAP_SERVICE_ADDRESS}/map`, request, {
      cancelToken
    });
    return response.data;
  } catch (error) {
    // Check if this is a cancellation error
    if (axios.isCancel(error)) {
      throw error; // Re-throw cancellation errors instead of returning null
    }

    return null;
  }
}

export interface MapEntity {
  uwi: string;
  color: string;
  value: string;
  group: string;
  hasSurvey: boolean;
}

export const fetchDefaultSizesForXDA = async (uwis) => {
  const slicedUwis = uwis.slice(0, 120);

  const s = encodeURIComponent(btoa(slicedUwis.join(",")));
  const endpoint = `${xdaRootUrl}/default-size?uwis=${s}`;
  const response = await axios.get<XdaConstraintT>(endpoint);
  return response.data;
};

/**
 * Shared React Query cache key for fetchEntitiesOnMap.
 * All callers must use this constant so react-query deduplicates the request
 * and serves from cache rather than issuing redundant network calls.
 *
 * Owner:      React Query (server state — see STATE_OWNERSHIP_CONTRACT.md §3.1)
 * Key shape:  [ENTITIES_ON_MAP_QUERY_KEY, txnId: string]
 */
export const ENTITIES_ON_MAP_QUERY_KEY = "entitiesOnMap" as const;

/**
 * staleTime for all fetchEntitiesOnMap queries.
 *
 * txnId is issued by the backend on every map/grouping state change and is
 * never reused, so data cached under a given txnId is immutable — it can never
 * become stale while the key is the same. Infinity prevents React Query from
 * scheduling background refetches for data that is guaranteed not to have
 * changed.
 */
export const ENTITIES_ON_MAP_STALE_TIME = Infinity;

/**
 * cacheTime for the map's server-state queries (entitiesOnMap, xdaProjection).
 *
 * Every filter/groupBy change reissues these under a new txnId far more often
 * than the default 5-minute react-query cacheTime, so stale entries piled up
 * and were retained as non-reclaimable heap. 60s is enough to survive a quick
 * back-and-forth without the cache growing unbounded during a sustained
 * map session.
 *
 * react-query resolves cacheTime as the MAX across every active observer of
 * a given key. Every useQuery/fetchQuery call on ENTITIES_ON_MAP_QUERY_KEY
 * must pass this constant — an observer left on the 5-minute default (or on
 * cacheTime: 0) silently wins the max and makes this bound inert everywhere
 * else that key is used.
 */
export const MAP_QUERY_CACHE_TIME = 60_000;

export const fetchRawMapEntities = async (txnId: string, wells: string[]) => {
  const endpoint = `${mapServiceEndpoint}/well/${txnId}/filter`;
  return await axios.post<MapEntity[]>(endpoint, wells);
};

export const BASE_WELL_PLAYS_QUERY_KEY = "baseWellPlays" as const;

/**
 * Every distinct resource play present in the given viewport bbox, among the
 * org's entitled base-layer sources — unfiltered, since this route no longer
 * takes a filterId (EVA-35 viewport rework dropped `f`/filter scoping from
 * this route entirely; the well-list filter never applied here to begin
 * with). Sorted server-side A→Z case-insensitively. `bbox` is required; the
 * server answers 503 while a session is still building (same semantics as
 * the tile endpoint).
 *
 * The client owns which plays are selected (EVA-35 viewport rework) — the
 * server no longer reports selection state at all (`inFilter` is gone from
 * the payload).
 */
export const fetchBaseWellPlays = async (bbox: string): Promise<string[]> => {
  const response = await axios.get<string[]>(
    `${mapServiceEndpoint}/basewelltile/plays?bbox=${encodeURIComponent(bbox)}`
  );
  return response.data ?? [];
};

export async function fetchMapEntities(
  txnId: string,
  wells: string[]
): Promise<Result<MapEntity[]>> {
  const endpoint = `${mapServiceEndpoint}/well/${txnId}/filter`;

  try {
    if (!(txnId && wells.length)) {
      return {
        ok: true,
        value: []
      };
    }
    const response = await axios.post<MapEntity[]>(endpoint, wells);

    if (response.status === 200) {
      return {
        ok: true,
        value: response.data
      };
    }

    return {
      ok: false,
      error: ""
    };
  } catch (err) {
    return {
      ok: false,
      error: err.response?.data ?? ""
    };
  }
}

function getGroupedPlayZonesByPeriod() {
  const url = `${mapServiceEndpoint}/ipdb/groupedPlayZones`;
  return axios.get(url, {});
}

function getIpdb(modelSource, selectedIpdbZone, selectedIpdbField) {
  if (!selectedIpdbZone || !selectedIpdbField) return null;

  if (selectedIpdbZone) {
    const url = `${mapServiceEndpoint}/ipdb/minmax`;
    return axios.post(url, {
      zones: selectedIpdbZone,
      modelSource,
      field: selectedIpdbField,
      wkt: ""
    });
  }
  return null;
}

function getIpdbPercentiles(
  modelSource: string,
  selectedIpdbZone: string[],
  selectedIpdbField: string
) {
  if (!selectedIpdbZone || !selectedIpdbField) return null;

  if (selectedIpdbZone) {
    const url = `${mapServiceEndpoint}/ipdb/p10p90`;
    return axios.post(url, {
      zones: selectedIpdbZone,
      modelSource,
      field: selectedIpdbField,
      wkt: ""
    });
  }
  return null;
}

function getExtrusion(
  txnId: TxnT,
  showBubble: boolean,
  bubbleField: { property: string; title: string; pdenSource?: PdenSourceEnum }[],
  showExtrude: boolean,
  bubbleOptions: BubbleOptionsT
) {
  if (!txnId || !txnId.id || !txnId.filterId || bubbleField.length == 0) return null;
  const fields = bubbleField
    .map((field) => `bubbleField=${encodeURIComponent(field.property)}`)
    .join("&");
  const url = `${mapServiceEndpoint}/map-vis/bubble/${txnId.filterId}?txnId=${
    txnId.id
  }&bubble=${showBubble}&${fields}&extrude=${showExtrude}&extrudeField=${encodeURIComponent(
    bubbleField[0].property
  )}&maxBubbleRadius=${bubbleOptions.maxRadiusSizeMeters}&location=${
    bubbleOptions.location
  }&pdenSource=${bubbleField[0].pdenSource ?? PdenSourceEnum.Public}`;
  return axios.get(url);
}

function getIpdbValueExtrusion(
  mapExtent,
  selectedIpdbField,
  selectedIpdbZone,
  useOldIpdb
) {
  if (!selectedIpdbField || !selectedIpdbZone) return null;

  const request = {
    GeoJsonArea: JSON.stringify(mapExtent),
    Field: selectedIpdbField,
    Zones: [selectedIpdbZone],
    useOldIpdb: useOldIpdb
  };
  return axios.post(`${mapServiceEndpoint}/ipdb/value/geojson`, request);
}

function getCompletion(filterId, field, maxVal, length) {
  const completionsUrl = `${dataRoot}/api/v1/frac/completion/${filterId}?field=${field}&maxValue=${maxVal}&maxPerfLengthMeters=${length}`;
  return axios.get(completionsUrl);
}

async function getTownshipByDls(values) {
  const locationUrl = `${mapServiceEndpoint}/Location/dls/${values.township}/${values.range}/${values.meridian}`;
  return axios.get(locationUrl);
}

async function getTownshipByNts(values) {
  const locationUrl = `${mapServiceEndpoint}/Location/nts/${values.block}/${values.sheet}/${values.mus}/${values.grid}`;
  return axios.get(locationUrl);
}

/** One entry of a Jump to Location dropdown. country is only sent for regions. */
export interface NamedOption {
  id: string;
  label: string;
  country?: Nullable<string>;
}

/** The survey system a region is surveyed by, which is the form the cascade draws for it. */
export type GridType = "plss" | "texas" | "county";

/** One region of the first cascade level, which is a US state. */
export interface RegionOption extends NamedOption {
  gridType: GridType;
}

/** A resolved place: its bounding box, an always-inside-the-polygon centroid, and a label. */
export interface PlaceLocation {
  value: string;
  minx: number;
  miny: number;
  maxx: number;
  maxy: number;
  centroidLng: number;
  centroidLat: number;
}

export interface PlssLocationParams {
  geoid: string;
  township: string;
  range: string;
  section?: Nullable<string>;
}

/**
 * What the user has picked in one of the other survey systems. The same selection answers
 * both questions the service is asked about it: which values the next dropdown offers, and
 * which place the whole thing names. Every level below the first is optional, because a
 * half-filled selection is what an options request is for.
 */
export interface TexasSelection {
  geoid: string;
  abstract?: Nullable<string>;
  block?: Nullable<string>;
  section?: Nullable<string>;
}

/** The dropdown being filled. The service reads it to know which list to answer with. */
export type TexasLevel = "abstract" | "block" | "section";

/**
 * The one country the cascade offers, which the county lookups are asked about and the
 * region list is filtered to.
 */
export const LOCATION_LOOKUP_COUNTRY = "US";

/**
 * Every reference data lookup behind Jump to Location, as the map service routes them.
 * The lookups below build their URLs from this list — the path they pass is typed by it —
 * so a lookup added to this client has to be named here first. Anything that needs to
 * recognise one of these requests reads the list rather than repeating it. `states` is the
 * service's older US-only list, which nothing here asks for any more.
 */
export const LOCATION_LOOKUP_PATHS = [
  "states",
  "regions",
  "counties",
  "county",
  "plss",
  "plss/townships",
  "plss/ranges",
  "plss/sections",
  "texas/options",
  "texas/place"
] as const;

type LocationLookupPath = (typeof LOCATION_LOOKUP_PATHS)[number];

/**
 * True when a URL is one of those lookups. The older jump actions sit under the same
 * prefix — Location/dls and Location/nts with the values in the path — so each lookup is
 * matched whole rather than by its first segment, which would cover those too.
 */
export function isLocationLookupUrl(url?: string): boolean {
  const path = url?.split("?")[0];
  if (!path) return false;
  return LOCATION_LOOKUP_PATHS.some((lookup) => path.endsWith(`/Location/${lookup}`));
}

// An empty value is left out rather than sent blank, because the service reads a blank
// parent as "not chosen yet" and a missing one the same way.
function locationLookupUrl(
  path: LocationLookupPath,
  params: Record<string, Nullable<string>>
) {
  const asked = Object.entries(params).filter(([, value]) => Boolean(value));
  const query = new URLSearchParams(asked as string[][]).toString();
  return `${mapServiceEndpoint}/Location/${path}${query ? `?${query}` : ""}`;
}

async function getRegions(): Promise<RegionOption[]> {
  const response = await axios.get<RegionOption[]>(locationLookupUrl("regions", {}));
  return response.data;
}

async function getCounties(country: string, state: string): Promise<NamedOption[]> {
  const response = await axios.get<NamedOption[]>(
    locationLookupUrl("counties", { country, state })
  );
  return response.data;
}

async function getCountyLocation(geoid: string): Promise<PlaceLocation> {
  const response = await axios.get<PlaceLocation>(locationLookupUrl("county", { geoid }));
  return response.data;
}

async function getPlssTownships(geoid: string): Promise<NamedOption[]> {
  const response = await axios.get<NamedOption[]>(
    locationLookupUrl("plss/townships", { geoid })
  );
  return response.data;
}

async function getPlssRanges(geoid: string, township: string): Promise<NamedOption[]> {
  const response = await axios.get<NamedOption[]>(
    locationLookupUrl("plss/ranges", { geoid, township })
  );
  return response.data;
}

async function getPlssSections(
  geoid: string,
  township: string,
  range: string
): Promise<NamedOption[]> {
  const response = await axios.get<NamedOption[]>(
    locationLookupUrl("plss/sections", { geoid, township, range })
  );
  return response.data;
}

async function getPlssLocation({
  geoid,
  township,
  range,
  section
}: PlssLocationParams): Promise<PlaceLocation> {
  // A township with no range names several places at once, so the lookup is refused here
  // rather than sent as a request the service can only answer with a 404.
  if (!geoid || !township || !range) {
    throw new Error("A township lookup needs a county, a township and a range");
  }
  const params = { geoid, township, range };
  if (section) {
    params["section"] = section;
  }
  const response = await axios.get<PlaceLocation>(locationLookupUrl("plss", params));
  return response.data;
}

// Each list is asked for with the levels it is narrowed by, and with nothing else: a county
// for the abstract and block lists, and a county and a block for the sections inside one.
async function getTexasOptions(
  level: TexasLevel,
  { geoid, block }: Pick<TexasSelection, "geoid" | "block">
): Promise<NamedOption[]> {
  const response = await axios.get<NamedOption[]>(
    locationLookupUrl("texas/options", { level, geoid, block })
  );
  return response.data;
}

async function getTexasLocation({
  geoid,
  abstract,
  block,
  section
}: TexasSelection): Promise<PlaceLocation> {
  const response = await axios.get<PlaceLocation>(
    locationLookupUrl("texas/place", { geoid, abstract, block, section })
  );
  return response.data;
}

// The query string is omitted when no value is supplied, so every existing
// caller keeps the service's own default half-length of 100 m each side.
function perpendicularLineUrl(uniqueId, halfLengthMeters?: number) {
  const query =
    halfLengthMeters == null
      ? ""
      : `?halfLengthMeters=${encodeURIComponent(halfLengthMeters)}`;
  return `${mapServiceEndpoint}/well/perpendicular-line/${uniqueId}${query}`;
}

async function getPerpendicularLine(uniqueId, halfLengthMeters?: number) {
  try {
    const response = await axios.get(perpendicularLineUrl(uniqueId, halfLengthMeters));
    if (response.status !== 200) {
      return null;
    }
    return response.data;
  } catch (error) {
    return null;
  }
}

/**
 * Same request as `getPerpendicularLine`, but a failed request rejects instead
 * of returning null. Callers that must tell "the request failed" apart from
 * "this well has no directional survey" need this one.
 */
async function fetchPerpendicularLine(
  uniqueId: string,
  halfLengthMeters?: number,
  signal?: AbortSignal
) {
  const response = await axios.get(perpendicularLineUrl(uniqueId, halfLengthMeters), {
    signal
  });
  return response.status === 200 ? response.data : null;
}

/**
 * The wells a two-point line crosses, keyed by UWI. Unscoped: the lasso path in
 * `mapboxHelper.ts` narrows the same response both to the wells the map is
 * rendering and to the active filter. This one does neither — its callers may
 * have no viewport to narrow against, and filter scoping is theirs to apply
 * (`useDeriveXdaLineForWell` does). A rejection is left for the caller too: a
 * silently empty result renders a wide, near-empty cross-section that reads as
 * a working one.
 */
async function postWellsIntersect(line: number[][]): Promise<Record<string, string>> {
  const polygon = {
    type: "Polygon",
    coordinates: [
      [
        [line[0][0], line[0][1]],
        [line[1][0], line[1][1]],
        [line[1][0], line[1][1]],
        [line[0][0], line[0][1]]
      ]
    ]
  };
  const response = await axios.post<Record<string, string>>(
    `${mapServiceEndpoint}/map/wells-intersect`,
    polygon
  );
  return response.data ?? {};
}

const ENTITIES_ON_MAP_WORKER_MIN_BYTES = 256 * 1024;

function shouldDecodeEntitiesOnMapInWorker(payload: ArrayBuffer | Uint8Array): boolean {
  if (process.env.NODE_ENV === "test") {
    return false;
  }

  if (typeof window === "undefined" || typeof Worker === "undefined") {
    return false;
  }

  return payload.byteLength >= ENTITIES_ON_MAP_WORKER_MIN_BYTES;
}

function captureEntitiesOnMapWorkerFailure(error: unknown, payloadByteLength: number) {
  Sentry.withScope((scope) => {
    scope.setTag("component", "fetchEntitiesOnMap");
    scope.setLevel("warning");
    scope.setExtra("payloadByteLength", payloadByteLength);
    Sentry.captureException(error);
  });
}

async function decodeEntitiesOnMapResponse(
  payload: ArrayBuffer | Uint8Array
): Promise<Record<string, EntitiesInGroupModel>> {
  if (shouldDecodeEntitiesOnMapInWorker(payload)) {
    try {
      const { decodeEntitiesOnMapInWorker } = await import("./entitiesOnMapWorkerClient");
      // Transfer a copy — postMessage detaches the buffer it's given, and `payload`
      // must stay intact for the fallback decode below if the worker call fails.
      const bufferForWorker = toEntitiesOnMapArrayBuffer(payload).slice(0);
      return await decodeEntitiesOnMapInWorker(bufferForWorker);
    } catch (error) {
      captureEntitiesOnMapWorkerFailure(error, payload.byteLength);
    }
  }

  return decodeEntitiesOnMapPayload(payload);
}

export async function fetchEntitiesOnMap(
  txnId: string
): Promise<Record<string, EntitiesInGroupModel>> {
  if (!txnId || txnId === CLEAR_MAP_ID) {
    return {};
  }
  const response = await axios.get(`${MAP_SERVICE_ADDRESS}/map/entities/${txnId}`, {
    responseType: "arraybuffer"
  });
  if (response.status !== 200) {
    throw new Error("unable to fetch entities on map.");
  }

  return await decodeEntitiesOnMapResponse(response.data);
}

/**
 * Shared React Query cache key for fetchGroupBounds.
 *
 * Owner:      React Query (server state — see STATE_OWNERSHIP_CONTRACT.md §3.1)
 * Key shape:  [GROUP_BOUNDS_QUERY_KEY, txnId: string, groupTitle: string]
 */
export const GROUP_BOUNDS_QUERY_KEY = "groupBounds";

/**
 * Bounding box for a single group, as returned by `POST /map/bbox/{txnId}`.
 * `.finite()` rejects NaN/Infinity/undefined at the boundary so a malformed
 * 200 response (e.g. `Bound.Sw`/`Ne` defaulting to `{}` server-side) can never
 * reach `mapbox.fitBounds`.
 */
export const GroupBoundsSchema = z.object({
  sw: z.tuple([z.number().finite(), z.number().finite()]), // [lon, lat]
  ne: z.tuple([z.number().finite(), z.number().finite()]) // [lon, lat]
});
export type GroupBounds = z.infer<typeof GroupBoundsSchema>;

const requestGroupBounds = async (
  txnId: string,
  groupTitle: string
): Promise<unknown> => {
  const { data } = await axios.post(
    `${MAP_SERVICE_ADDRESS}/map/bbox/${encodeURIComponent(txnId)}`,
    { groupTitle }
  );
  return data;
};

/**
 * Imperative fetch (through React Query for caching/dedup — server data must
 * go through React Query per STATE_OWNERSHIP_CONTRACT.md §3.1/§7.1/§12.4) of
 * the bounding box for a double-clicked group. `null` means "no-op": either a
 * legitimate empty/unknown group (404, silent), a transient UWI-lookup-table-
 * loading state (503, retried up to 3x then a non-error "try again" toast), or a
 * real failure (reported to Sentry + error toast) — callers should not
 * distinguish these cases, they should just skip the `fitBounds` call.
 */
export const fetchGroupBounds = (
  queryClient: QueryClient,
  txnId: string,
  groupTitle: string
): Promise<Nullable<GroupBounds>> =>
  queryClient
    .fetchQuery({
      queryKey: [GROUP_BOUNDS_QUERY_KEY, txnId, groupTitle],
      queryFn: () => requestGroupBounds(txnId, groupTitle),
      staleTime: Infinity, // txn is immutable
      // Backend deliberately returns 503 while the UWI lookup table is
      // (re)loading and expects the client to retry — not error-toast. Every
      // other status (404 included) should fail fast: no point retrying a
      // legitimate empty/unknown group.
      retry: (failureCount, error) =>
        axios.isAxiosError(error) && error.response?.status === 503 && failureCount < 3
    })
    .then((data) => {
      const parsed = GroupBoundsSchema.safeParse(data);
      if (parsed.success) {
        return parsed.data;
      }
      // 200 with malformed/empty body = backend contract breach (empty group
      // should be a 404). Log for devs; no-op for the user.
      Sentry.captureException(new Error("Invalid /map/bbox response shape"));
      return null;
    })
    .catch((error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null; // empty/unknown group — silent no-op is correct
      }
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        // Transient: UWI lookup table still loading and retries are exhausted.
        // Surface a distinct, non-error notice so the user knows to retry — this
        // is NOT a silent no-op like a legitimately empty group (404), and NOT a
        // real failure (no Sentry). Keeps the "retry shortly" 503 contract visible.
        toast.info("Map data is still loading — try again in a moment.");
        return null;
      }
      Sentry.withScope((scope) => {
        scope.setContext("fetchGroupBounds", { txnId, groupTitle });
        scope.setLevel("error");
        Sentry.captureException(error);
      });
      toast.error("Couldn't zoom to group");
      return null;
    });

export async function fetchXdaProjection(
  request: XdaProjectionRequest
): Promise<XdaProjectionResponse> {
  const response = await axios.post<XdaProjectionResponse>(
    `${MAP_RS_SERVICE_ADDRESS}/xda-projection`,
    request
  );
  const parsed = XdaProjectionResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    const { logger } = Sentry;
    logger.error("XDA projection response validation failed", {
      error: parsed.error.message,
      issues: parsed.error.issues
    });

    return { type: "FeatureCollection", features: [] };
  }

  return parsed.data;
}

export {
  getTxnId,
  getIpdb,
  getIpdbPercentiles,
  getIpdbValueExtrusion,
  getExtrusion,
  getCompletion,
  getPerpendicularLine,
  fetchPerpendicularLine,
  postWellsIntersect,
  getTownshipByDls,
  getTownshipByNts,
  getRegions,
  getCounties,
  getCountyLocation,
  getPlssTownships,
  getPlssRanges,
  getPlssSections,
  getPlssLocation,
  getTexasOptions,
  getTexasLocation,
  getGroupedPlayZonesByPeriod
};
